import { ConsentAttribute } from '@epilot/entity-client'
import { TFunction } from 'i18next'
import { omit, compose, orderBy, flatMap, filter, get } from 'lodash/fp'
import { v4 as uuid } from 'uuid'

import {
  Attribute as APIAttributes,
  EntityItem,
  EntitySchemaItem
} from 'api/ApiTypes'
import { getCustomerApiClient, PORTAL_TYPES } from 'api/CustomerApiClient'
import { CUSTOMER_QUERY_IDS } from 'api/Queries'
import { getDeliveryAddress } from 'components/requests/utils'
import {
  formatBoolean,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatDefault,
  formatNumber,
  formatRelation,
  formatStatus,
  formatString
} from 'components/shared/attributes/attribute/formatter'
import {
  getRelationAddress,
  getRelationPayments,
  getRepeatedValue
} from 'components/shared/attributes/attribute/getter'
import { isReallyNaN } from 'components/shared/attributes/attribute/utils'
import { type Attribute as ConfigAttributes } from 'components/shared/attributes/types'
import {
  ConsentStatus,
  Contact,
  PendingChange,
  RepeatableAddress,
  RepeatableEmail,
  RepeatablePhone
} from 'context/ContactContext'
import useEcpTranslation from 'hooks/common/useEcpTranslation'

import { getCustomSchemaAttributes, isCustomSchemaType } from './AttributeUtils'
import { formatAddress } from './CommonUtils'
import {
  RepeatablePaymentOption,
  RepeatablePaymentOptionType
} from './PaymentUtils'
import RouteUtils from './RouteUtils'
import { Flow, Workflow, WorkflowStep } from './WorkflowUtils'

export type Attribute = APIAttributes & ConfigAttributes

export enum EntityType {
  CONTACT = 'contact',
  ORDER = 'order',
  OPPORTUNITY = 'opportunity',
  CONTRACT = 'contract',
  METER = 'meter',
  METER_COUNTER = 'meter_counter',
  ACCOUNT = 'account',
  DOCUMENT = 'file',
  BILLING_EVENT = 'billing_event'
}

export const generateKey = () => {
  return Math.random().toString(36).substring(2, 9)
}

const mapContactWithRepeatableIds = (contact: Contact): Contact => {
  const _pending_changes = { ...contact?._pending_changes }

  return {
    ...contact,
    email: Array.isArray(contact?.email)
      ? contact?.email?.map((email: RepeatableEmail) => ({
          ...email,
          id: generateKey()
        }))
      : ([] as RepeatableEmail[]),
    phone: Array.isArray(contact?.phone)
      ? contact?.phone?.map((phone: RepeatablePhone) => ({
          ...phone,
          id: generateKey()
        }))
      : ([] as RepeatablePhone[]),
    address: Array.isArray(contact?.address)
      ? contact?.address?.map((address: RepeatableAddress) => ({
          ...address,
          id: generateKey()
        }))
      : ([] as RepeatableAddress[]),
    payment: Array.isArray(contact?.payment)
      ? contact?.payment?.map((payment: RepeatablePaymentOption) => ({
          ...payment,
          id: generateKey()
        }))
      : ([] as RepeatablePaymentOption[]),
    _pending_changes
  }
}

export const createPendingChange = (newValue: unknown): PendingChange => {
  return {
    new_value: newValue,
    requested_at: new Date(Date.now()).toISOString(),
    requested_by: 'ecp'
  }
}

const unmapContactOfRepeatableIds = (contact: Contact): Contact => {
  return {
    ...contact,
    email: Array.isArray(contact?.email)
      ? contact?.email?.map(
          (email: RepeatableEmail) => omit('id')(email) as RepeatableEmail
        )
      : ([] as RepeatableEmail[]),
    phone: Array.isArray(contact?.phone)
      ? contact?.phone?.map(
          (phone: RepeatablePhone) => omit('id')(phone) as RepeatablePhone
        )
      : ([] as RepeatablePhone[]),
    address: Array.isArray(contact?.address)
      ? contact?.address?.map(
          (address: RepeatableAddress) =>
            omit('id')(address) as RepeatableAddress
        )
      : ([] as RepeatableAddress[]),
    payment: Array.isArray(contact?.payment)
      ? contact?.payment?.map(
          (payment: RepeatablePaymentOption) =>
            omit('id')(payment) as RepeatablePaymentOption
        )
      : ([] as RepeatablePaymentOption[])
  }
}

const emptyRepeatable = {
  email: () => ({
    id: generateKey(),
    _tags: [],
    email: ''
  }),
  phone: () => ({
    id: generateKey(),
    _tags: [],
    phone: ''
  }),
  address: () => ({
    id: generateKey(),
    _tags: [],
    country: '',
    city: '',
    postal_code: '',
    street: '',
    street_number: '',
    additional_info: ''
  }),
  paymentInvoice: () => ({
    id: generateKey(),
    _id: uuid(),
    _tags: [],
    type: RepeatablePaymentOptionType.INVOICE
  }),
  paymentCash: () => ({
    id: generateKey(),
    _id: uuid(),
    _tags: [],
    type: RepeatablePaymentOptionType.CASH
  }),
  paymentSEPA: (): RepeatablePaymentOption => ({
    id: generateKey(),
    _id: uuid(),
    _tags: [],
    type: RepeatablePaymentOptionType.SEPA,
    data: {
      bank_name: '',
      iban: '',
      bic_number: '',
      fullname: ''
    }
  })
}

interface shouldWorkflowStepBeVisible {
  step: WorkflowStep
  origin: string
}

const shouldWorkflowStepBeVisible = (
  params: shouldWorkflowStepBeVisible
): boolean => {
  if (params.origin === PORTAL_TYPES.endCustomerPortal) {
    // Default 'enabled' property to TRUE if it doesn't exist on the object at all
    // This is a backwards compatibility mechanism for Workflow Steps that contain
    // ECP label but no 'enabled' property
    if (params.step?.ecp && !('enabled' in params.step.ecp)) {
      return !!params.step?.ecp?.label
    }

    return params.step.ecp?.enabled && !!params.step?.ecp?.label
  }

  if (params.origin === PORTAL_TYPES.installerPortal) {
    // Default 'enabled' property to TRUE if it doesn't exist on the object at all
    // This is a backwards compatibility mechanism for Workflow Steps that contain
    // INSTALLER label but no 'enabled' property
    if (params.step?.installer && !('enabled' in params.step.installer)) {
      return !!params.step?.installer?.label
    }

    return params.step.installer?.enabled && !!params.step?.installer?.label
  }

  return params.step.ecp?.enabled && !!params.step?.ecp?.label
}

const useGetWorkflow = (
  workflows: Workflow[],
  origin: string
): { workflows: { steps: WorkflowStep[]; workflow: Workflow }[] } => {
  // Backwards compatibility with new workflow ecp information
  if (!workflows) {
    return null
  }

  const ecpWorkflows = compose(
    orderBy(
      (workflow: Workflow) => new Date(workflow.lastUpdateTime),
      ['desc']
    ),
    filter((workflow: Workflow) => {
      const hasEcpStep = workflow?.flow?.find((flow: any) => {
        if (flow?.type === 'SECTION') {
          return flow?.steps?.find((sectionStep: WorkflowStep) => {
            return shouldWorkflowStepBeVisible({
              step: sectionStep,
              origin
            })
          })
        }

        if (flow?.type === 'STEP') {
          return shouldWorkflowStepBeVisible({
            step: flow,
            origin
          })
        }

        throw new Error('Workflow has a step which is of unknown type')
      })

      return !!hasEcpStep
    })
  )(workflows) as Workflow[]

  const allWorkflows = ecpWorkflows.map((workflow) => {
    return {
      workflow: workflow,
      steps: getWorkflowSteps(workflow?.flow, origin)
    }
  })

  return {
    workflows: allWorkflows
  }
}

const getWorkflowSteps = (flow: Flow[], origin: string): WorkflowStep[] => {
  if (!flow) {
    return []
  }

  const stepsInRightOrder = flatMap((item: any) => {
    if (item.type === 'STEP') {
      return item
    }

    return item.steps
  })(flow)

  return stepsInRightOrder.filter((step: WorkflowStep) => {
    if (origin === PORTAL_TYPES.endCustomerPortal) {
      if (step.ecp?.enabled || (step?.ecp && !('enabled' in step.ecp))) {
        return !!step?.ecp?.label
      }
    }

    if (origin === PORTAL_TYPES.installerPortal) {
      if (
        step.installer?.enabled ||
        (step?.installer && !('enabled' in step.installer))
      ) {
        return !!step?.installer?.label
      }
    }

    return false
  })
}

const getEntityQueryIdBySchema = (schema: string): string => {
  const queryBySchemaMap = {
    contact: 'GET_CONTACT',
    opportunity: 'GET_OPPORTUNITY_DETAILS',
    order: 'GET_ORDER_DETAILS',
    contract: 'GET_CONTRACT_DETAILS',
    meter: 'GET_METER_DETAILS'
  }

  return queryBySchemaMap[schema]
}

type QueryBySchema = {
  queryId: string[]
  queryFn: () => Promise<any>
}

export const getGetAllQueryCalBySchema = (schema: string): QueryBySchema => {
  const queryBySchemaMap = {
    opportunity: {
      queryId: [CUSTOMER_QUERY_IDS.GET_ALL_OPPORTUNITIES],
      queryFn: getCustomerApiClient().getAllOpportunities
    },
    order: {
      queryId: [CUSTOMER_QUERY_IDS.GET_ALL_ORDERS],
      queryFn: getCustomerApiClient().getAllOrders
    },
    contract: {
      queryId: [CUSTOMER_QUERY_IDS.GET_CONTRACTS],
      queryFn: getCustomerApiClient().getAllContracts
    }
  } as const

  return queryBySchemaMap[schema]
}

export const getRouteBySchema = (schema: string) => {
  switch (schema) {
    case 'contact':
      return RouteUtils.myAccountRoute()
    case 'contract':
      return RouteUtils.contractsRoute()
    case 'opportunity':
      return RouteUtils.opportunitiesRoute()
    case 'order':
      return RouteUtils.ordersRoute()
    case 'meter':
      return RouteUtils.metersRoute()
    default:
      return ''
  }
}

export const getMockDataBySchema = (schema: string) => {
  switch (schema) {
    case 'contract':
      return []
    case 'opportunity':
      return []
    case 'order':
      return []
    case 'meter':
      return []
    default:
      return []
  }
}

export const showAttributesBySchema = (schema: string) => {
  switch (schema) {
    case 'contract':
      return ['contract_name', 'status', '_created_at']
    case 'opportunity':
      return ['opportunity_name', 'status', '_created_at']
    case 'order':
      return ['order_name', 'status', '_created_at']
    case 'meter':
      return ['meter_name', 'status', '_created_at']
    default:
      return []
  }
}

export const getAttributeByName = (
  schemas: EntitySchemaItem[],
  slug: string,
  name: string
) => {
  const schemaItem = schemas.find((s) => s.slug === slug)

  return schemaItem?.attributes?.find((a) => a.name === name)
}

export const useIdentifierToLabel = () => {
  const { t } = useEcpTranslation()

  return (identifier: string) => {
    const identifierLabel = identifier
      .split('_')
      .join(' ')
      .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())

    return t(`attributes.${identifierLabel}`, { defaultValue: identifierLabel })
  }
}

export const getFormattedValue = (
  attribute: Attribute,
  t: TFunction,
  entity: EntityItem
) => {
  if (!attribute) return ''

  return getValue({
    t,
    attribute,
    entity: entity as EntityItem,
    relations: [],
    emptyValue: ''
  })
}

export const getFormattedValueWithRelations = (
  schemas: EntitySchemaItem[],
  slug: string,
  fieldName: string,
  entity: EntityItem,
  relations: EntityItem[],
  t: TFunction
) => {
  if (!fieldName) return ''

  if (!fieldName.includes('.')) {
    const attribute = getAttributeForField(schemas, slug, fieldName)

    if (!attribute) return ''

    return getValue({
      t,
      attribute,
      emptyValue: '',
      entity,
      relations
    })
  }

  const [relationFieldName, targetFieldName] = fieldName.split('.', 2)

  const relationAttribute = getAttributeByName(schemas, slug, relationFieldName)

  if (!relationAttribute) {
    return ''
  }

  if (
    relationAttribute.type !== 'relation' &&
    relationAttribute.type !== 'relation_address' &&
    relationAttribute.type !== 'address'
  ) {
    return ''
  }

  const relationData = get(relationFieldName)(entity)

  if (
    !relationData ||
    !Array.isArray(relationData) ||
    relationData.length === 0
  ) {
    return ''
  }

  const relatedEntityData = relationData[0]

  if (!relatedEntityData) {
    return ''
  }

  let targetSchema = (relationAttribute as any).target

  if (relationAttribute.type === 'address') {
    targetSchema = 'address'
  }

  if (!targetSchema) {
    const value = get(targetFieldName)(relatedEntityData)

    return value ? String(value) : ''
  }

  let targetAttribute = getAttributeByName(
    schemas,
    targetSchema,
    targetFieldName
  )

  if (!targetAttribute && isCustomSchemaType(targetSchema)) {
    const customAttributes = getCustomSchemaAttributes(targetSchema)

    targetAttribute = customAttributes.find(
      (attr) => attr.name === targetFieldName
    )
  }

  if (!targetAttribute) {
    const value = get(targetFieldName)(relatedEntityData)

    return value ? String(value) : ''
  }

  const mockRelatedEntity = {
    ...relatedEntityData,
    _schema: targetSchema,
    _id: relatedEntityData._id || 'unknown',
    _org: entity._org,
    _created_at: relatedEntityData._created_at || '',
    _updated_at: relatedEntityData._updated_at || ''
  }

  return getValue({
    t,
    attribute: targetAttribute,
    entity: mockRelatedEntity,
    relations,
    emptyValue: ''
  })
}

export const getAttributeForField = (
  schemas: EntitySchemaItem[],
  slug: string,
  fieldName: string
): Attribute | null => {
  if (!fieldName) return null

  // This is needed since the title is not in the schema attributes
  if (fieldName === '_title') {
    return {
      group: '',
      id: '',
      info_helpers: { hint_text: '' },
      label: 'Title',
      name: '_title',
      type: 'string'
    }
  }

  return getAttributeByName(schemas, slug, fieldName)
}

export const getAttributeForFieldsWithRelations = (
  schemas: EntitySchemaItem[],
  slug: string,
  fieldName: string,
  entity: EntityItem,
  relations: EntityItem[] = []
): { attribute: Attribute | null; relatedEntity: EntityItem | null } => {
  if (!fieldName) return { attribute: null, relatedEntity: null }

  if (!fieldName.includes('.')) {
    return {
      attribute: getAttributeForField(schemas, slug, fieldName),
      relatedEntity: null
    }
  }

  const [relationFieldName, targetFieldName] = fieldName.split('.', 2)

  const relationAttribute = getAttributeByName(schemas, slug, relationFieldName)

  if (!relationAttribute || relationAttribute.type !== 'relation') {
    return { attribute: null, relatedEntity: null }
  }

  const relationValue = get(relationFieldName)(entity)

  if (!relationValue?.$relation || !Array.isArray(relationValue.$relation)) {
    return { attribute: null, relatedEntity: null }
  }

  const relationRef = relationValue.$relation[0]

  if (!relationRef?.entity_id) {
    return { attribute: null, relatedEntity: null }
  }

  const relatedEntity = relations.find(
    (relation) => relation._id === relationRef.entity_id
  )

  if (!relatedEntity) {
    return { attribute: null, relatedEntity: null }
  }

  const targetAttribute = getAttributeByName(
    schemas,
    relatedEntity._schema,
    targetFieldName
  )

  return {
    attribute: targetAttribute,
    relatedEntity: relatedEntity
  }
}

export const getValue = (props: {
  t: TFunction
  attribute: Attribute
  entity: EntityItem
  relations: EntityItem[]
  emptyValue?: string
}) => {
  const { attribute, entity, relations, t, emptyValue } = props

  let value: any = get(attribute.name)(entity)

  switch (attribute.type as Attribute['type']) {
    case 'boolean':
    case 'checkbox': {
      value = formatBoolean(value, t, emptyValue)
      break
    }

    case 'date': {
      value = formatDate(value, emptyValue)
      break
    }

    case 'datetime': {
      value = formatDateTime(value, emptyValue)
      break
    }

    case 'relation': {
      value = formatRelation(value, relations, emptyValue)
      break
    }

    case 'relation_payment_method': {
      value = getRelationPayments(value, relations)
      break
    }

    case 'payment': {
      break
    }

    case 'address': {
      value = formatAddress(value?.[0])
      break
    }

    case 'relation_address': {
      if (relations?.length) {
        value = getRelationAddress(value, relations)
      } else {
        value = getDeliveryAddress(entity)
      }
      break
    }

    case 'currency': {
      value = formatCurrency(value, entity, attribute, emptyValue)
      if (isReallyNaN(value)) {
        value = emptyValue
      }
      break
    }

    case 'number': {
      value = formatNumber(value, entity, attribute, t, emptyValue)
      break
    }

    case 'status': {
      const options = get('options')(attribute)

      const optionValue =
        Array.isArray(options) && options?.find((o) => o.value === value)?.value

      value = formatStatus(optionValue || value, t, emptyValue)

      break
    }

    case 'select': {
      const options = get('options')(attribute)
      let labelValue = options?.find((o) => o?.value === value)?.title

      if (
        entity?._schema === EntityType.METER ||
        entity?._schema === EntityType.METER_COUNTER
      ) {
        labelValue = value
      }

      if (labelValue) {
        labelValue = t(`attributes.${value}`, labelValue)
      }

      value = formatString(labelValue, emptyValue)
      break
    }

    case 'phone':
    case 'email':
    case 'string': {
      if (Array.isArray(value)) {
        value = getRepeatedValue(attribute, value)
      } else {
        value = formatString(value, emptyValue)
      }
      break
    }

    case 'link': {
      break
    }

    case 'consent': {
      const hasGranularIdentifiers =
        Array.isArray(value.identifiers) && value.identifiers.length > 0

      if (
        hasGranularIdentifiers &&
        (attribute as ConsentAttribute).topic !== 'PRINT_MARKETING'
      ) {
        value = value.identifiers
          .filter((identifier) => identifier.status === ConsentStatus.Active)
          .map((identifier) => identifier.identifier)
          .join(', ')

        value = value ? t('consent_for', { value }) : emptyValue
      } else {
        value =
          value.status === ConsentStatus.Active
            ? t('consent_granted')
            : emptyValue
      }

      break
    }

    default: {
      value = formatDefault(value, emptyValue)
      break
    }
  }

  return value
}

export default {
  generateKey,
  mapContactWithRepeatableIds,
  unmapContactOfRepeatableIds,
  emptyRepeatable,
  useGetWorkflow,
  getEntityQueryIdBySchema,
  getGetAllQueryCalBySchema,
  getValue,
  getFormattedValue,
  getFormattedValueWithRelations,
  getAttributeForFieldsWithRelations
}
